import dayjs from "dayjs";
import { useEffect, useState } from "react";
import ReactApexChart from "react-apexcharts";
import { Button, CheckPicker, DateRangePicker, Divider, FormGroup, Input, Notification, Panel } from "rsuite";
import { ValueType } from "rsuite/lib/DateRangePicker";
import { ParametersGlobals } from "../../../../core/parametersGlobals/ParametersGlobals";
import axios from "../../../../utilities/axios";
import styles from "./styles/rhChart.module.scss";

interface RHChartProps { }

export default function RHChart({ }: RHChartProps) {
    const [state, setState] = useState<any>(null);
    const [storeChartState, setStoreChartState] = useState<any>(null);
    const [period, setPeriod] = useState<ValueType | undefined>([
        dayjs().startOf('month').toDate(),
        dayjs().endOf('month').toDate(),
    ]);
    const [selectedErrors, setSelectedErrors] = useState<number[]>([]);
    const [errorsData, setErrorsData] = useState<any[]>([]);
    const [loadingButton, setLoadingButton] = useState<boolean>(false);
    const [storeChartLoading, setStoreChartLoading] = useState<boolean>(false);
    const [showStoreChart, setShowStoreChart] = useState<boolean>(false);
    const [showEmployeeChart, setShowEmployeeChart] = useState<boolean>(false);
    const [employeeChartLoading, setEmployeeChartLoading] = useState<boolean>(false);
    const [employeeChartState, setEmployeeChartState] = useState<any>(null);
    const [employeeDetailsLoading, setEmployeeDetailsLoading] = useState<boolean>(false);
    const [showEmployeeDetails, setShowEmployeeDetails] = useState<boolean>(false);
    const [employeeDetailSelectedName, setEmployeeDetailSelectedName] = useState<string>("");
    const [employeeDetailChart, setEmployeeDetailChart] = useState<any>(null);

    useEffect(() => {
        try {
            fetchSelectErrorData()
        } catch (error) {
            console.log(error);
            Notification.error({ title: 'Erro', description: 'Erro ao buscar Lançamentos', placement: 'topEnd', close: true });
        }
    }, [])

    const fetchSelectErrorData = async () => {
        try {
            const response = await axios.get(`/api/v1/dashboard/chart/financial/custom/select-error-data`, {
                params: {
                    groupErrors: ParametersGlobals.defaultRHGroupErrorID
                }
            });

            const filteredErrors = response.data
                .filter((error: any) => error.error_active === true)
                .map((error: any) => ({
                    value: error.error_id,
                    label: error.error_name
                }));

            const allErrorIds = filteredErrors.map((e: any) => e.value);

            setErrorsData(filteredErrors);
            setSelectedErrors(allErrorIds); // Marca todos por padrão
        } catch (error) {
            console.log(error);
            Notification.error({ title: 'Erro', description: 'Erro ao buscar Lançamentos', placement: 'topEnd', close: true });
        }
    };

    const fetchChartData = async () => {
        try {
            const { data } = await axios.get("/api/v1/dashboard/rh/group_error_chart", {
                params: {
                    errorGroup: ParametersGlobals.defaultRHGroupErrorID,
                    error: selectedErrors,
                    periodRangeFilter: period
                }
            });

            const updatedOptions = {
                ...data.chartData,
                options: {
                    ...data.chartData.options,
                    chart: {
                        ...(data.chartData.options.chart || {}),
                        zoom: {
                            enabled: false,
                        },
                        events: {
                            dataPointSelection: function (event: any, chartContext: any, config: any) {
                                const index = config.dataPointIndex;
                                const errorName = data.chartData.options?.labels?.[index];

                                if (errorName) {
                                    handleSearchStoreChart(errorName);
                                }
                            }
                        }
                    }
                }
            };

            setState(updatedOptions)
            setShowStoreChart(false)
            setShowEmployeeChart(false)
        } catch (error) {
            console.error(error);

            Notification.error({
                title: "Erro!",
                description: "Erro ao buscar as informações do gráfico financeiro.",
                duration: 3500,
                placement: "topEnd"
            });
        } finally {
            setShowEmployeeDetails(false)
        }
    };

    const handleSearchStoreChart = async (errorName: string) => {
        try {
            setShowEmployeeChart(false)
            setShowStoreChart(true)
            setStoreChartLoading(true)
            setShowEmployeeDetails(false)

            const response = await axios.get(`/api/v1/dashboard/rh/store-error-chart`, {
                params: {
                    errorName,
                    periodRangeFilter: period
                }
            });

            const updatedOptions = {
                ...response.data.chartData,
                options: {
                    ...response.data.chartData.options,
                    chart: {
                        ...(response.data.chartData.options.chart || {}),
                        zoom: {
                            enabled: false,
                        },
                        events: {
                            dataPointSelection: function (event: any, chartContext: any, config: any) {
                                const index = config.dataPointIndex;
                                const clickedStore = response.data.chartData.customData?.[index];

                                if (clickedStore) {
                                    handleSearchEmployeeChart(errorName, clickedStore.id)
                                }
                            }
                        }
                    }
                }
            };

            setStoreChartState(updatedOptions)
            setShowStoreChart(true)
        } catch (error) {
            console.log(error)
        } finally {
            setStoreChartLoading(false)
        }
    }

    const handleSearchEmployeeChart = async (errorName: string, clickedStoreId: string) => {
        try {
            setEmployeeChartLoading(true)
            setShowEmployeeDetails(false)

            const response = await axios.get(`/api/v1/dashboard/rh/employee-error-chart`, {
                params: {
                    errorName,
                    periodRangeFilter: period,
                    storeId: clickedStoreId
                }
            });

            const updatedOptions = {
                ...response.data.chartData,
                options: {
                    ...response.data.chartData.options,
                    chart: {
                        ...(response.data.chartData.options.chart || {}),
                        zoom: {
                            enabled: false,
                        },
                        events: {
                            dataPointSelection: function (event: any, chartContext: any, config: any) {
                                const index = config.dataPointIndex;
                                const clickedEmployee = response.data.chartData.customData?.[index];

                                console.log({ clickedEmployee })

                                if (clickedEmployee) {
                                    handleSearchEmployeeDetails(errorName, clickedStoreId, clickedEmployee.id)
                                }
                            }
                        }
                    }
                }
            };

            setEmployeeChartState(updatedOptions)
            setShowEmployeeChart(true)
        } catch (error) {
            console.log(error)
        } finally {
            setEmployeeChartLoading(false)
        }
    }

    const handleSearchEmployeeDetails = async (errorName: string, clickedStoreId: string, clickedEmployeeId: string) => {
        try {
            setShowEmployeeDetails(true)
            setEmployeeDetailsLoading(true)

            const { data } = await axios.get(`/api/v1/dashboard/rh/employee-error-details`, {
                params: {
                    errorName,
                    periodRangeFilter: period,
                    storeId: clickedStoreId,
                    employeeId: clickedEmployeeId
                }
            });

            // Reconstruir os labels com contagem
            const rawData = data.chartData.rawData || [];

            const formattedLabels = data.chartData.options.labels.map((label: string) => {
                const match = rawData.find((item: any) => item.day === label);
                const count = match?.count ?? 0;
                return `${label}: ${count}`;
            });

            data.chartData.options.labels = formattedLabels;

            // Tooltip personalizada
            data.chartData.options.tooltip = {
                y: {
                    formatter: function () {
                        return ``;
                    },
                    title: {
                        formatter: (seriesName: any) => seriesName,
                    },
                }
            }

            setEmployeeDetailChart(data.chartData)
            setEmployeeDetailSelectedName(data.employeeName)
            setEmployeeDetailsLoading(false)
        } catch (error) {
            console.log(error)
        } finally {
            setEmployeeDetailsLoading(false)
        }
    }

    const handleFilterChart = async () => {
        try {
            setLoadingButton(true)
            await fetchChartData()
        } catch (error) {
            console.log(error)
        } finally {
            setLoadingButton(false)
        }
    }

    const handleClearFilters = () => {
        const allErrorIds = errorsData.map(e => e.value);

        // Limpar filtros
        setPeriod([
            dayjs().startOf('month').toDate(),
            dayjs().endOf('month').toDate(),
        ]);
        setSelectedErrors(allErrorIds); // Restaura todos os Lançamentosmentos marcados

        // Resetar gráficos e estados relacionados
        setState(null);
        setStoreChartState(null);
        setEmployeeChartState(null);

        setShowStoreChart(false);
        setShowEmployeeChart(false);
        setShowEmployeeDetails(false);

        setEmployeeDetailSelectedName("");
    };

    return (
        <>
            <div className={styles.container}>
                <h3>Gráfico RH</h3>

                <Panel
                    bordered
                    style={{ backgroundColor: '#f9f9f9', borderRadius: '10px' }}
                >
                    <div className={styles.filters}>
                        <FormGroup>
                            <label>Período</label>
                            <DateRangePicker
                                placeholder="Selecione uma data"
                                format="DD/MM/YYYY"
                                ranges={[]}
                                value={period}
                                onChange={(value) => setPeriod(value)}
                                style={{ width: 250 }}
                            />
                            <span className={styles.informationText}>* Obrigatório</span>
                        </FormGroup>

                        <FormGroup>
                            <label>Grupo de Lançamentos</label>
                            <Input
                                value="RH"
                                style={{ width: 150 }}
                                disabled
                            />
                            <span className={styles.informationText}>
                                * Obrigatório
                            </span>
                        </FormGroup>

                        <FormGroup style={{ width: '100%' }}>
                            <label>Lançamentos</label>
                            <CheckPicker
                                data={errorsData}
                                placeholder="Selecione os Lançamentos..."
                                cleanable
                                searchable
                                value={selectedErrors}
                                onChange={(value) => setSelectedErrors(value)}
                                style={{ width: '100%' }}
                            />
                            <span className={styles.informationText}>
                                * Se nenhum filtro for aplicado, todos os registros serão incluídos
                            </span>
                        </FormGroup>

                        <Button appearance="primary" color="cyan" onClick={handleClearFilters} style={{ width: 175, marginTop: '4px' }}>
                            Limpar
                        </Button>

                        <Button appearance="primary" onClick={handleFilterChart} loading={loadingButton} style={{ width: 175, marginTop: '4px' }}>
                            Filtrar
                        </Button>
                    </div>

                    <div className={styles.chartsContainer}>
                        {state ? (
                            <>
                                {/* <div className={styles.headerSummary}>
                                    <div className={styles.summaryCard}><TrendingUp size={20} /> Total: R$ {chartTotal.toFixed(2)}</div>
                                    <div className={styles.summaryCard}><Store size={20} /> Lojas: {Array.isArray(storeChartState?.series) ? storeChartState.series.length : 0}</div>
                                    <div className={styles.summaryCard}><Users size={20} /> Funcionários: {Array.isArray(employeeChartState?.series) ? employeeChartState.series.length : 0}</div>
                                </div> */}

                                <div className={styles.mainChartCard}>
                                    <h4 className={styles.chartTitle}>Gráfico por Lançamentos</h4>
                                    <ReactApexChart options={state.options} series={state.series} type="pie" height={500} />
                                </div>
                            </>
                        ) : (
                            <div className={styles.noDataContainer}>
                                <p className={styles.noDataText}>Nenhum dado encontrado</p>
                            </div>
                        )}

                        <div className={styles.secundaryChartsContainer}>
                            {showStoreChart && storeChartState && (
                                <div className={styles.secundaryChartCard}>
                                    <h5 className={styles.chartTitle}>Por Loja</h5>
                                    {storeChartLoading ? (
                                        <p className={styles.loadingText}>Carregando gráfico da loja...</p>
                                    ) : storeChartState ? (
                                        <ReactApexChart options={storeChartState.options} series={storeChartState.series} type="pie" height={400} />
                                    ) : null}
                                </div>
                            )}

                            {showEmployeeChart && employeeChartState && (
                                <div className={styles.secundaryChartCard}>
                                    <h5 className={styles.chartTitle}>Por Funcionário</h5>
                                    {employeeChartLoading ? (
                                        <p className={styles.loadingText}>Carregando gráfico de funcionários...</p>
                                    ) : employeeChartState ? (
                                        <ReactApexChart options={employeeChartState.options} series={employeeChartState.series} type="pie" height={400} />
                                    ) : null}
                                </div>
                            )}
                        </div>

                        {showEmployeeDetails && (
                            <div className={styles.employeeDetailsList}>
                                <Divider>Detalhes do funcionário: <strong>{employeeDetailSelectedName}</strong></Divider>

                                {employeeDetailsLoading ? (
                                    <p className={styles.loadingText}>Carregando detalhes dos funcionários...</p>
                                ) : (
                                    <>
                                        {employeeDetailChart && employeeDetailChart.series.length > 0 && (
                                            <ReactApexChart options={employeeDetailChart.options} series={employeeDetailChart.series} type="pie" height={400} />
                                        )}
                                    </>

                                    // <ul className={styles.employeeDetailsListContainer}>
                                    //     {employeeDetails?.map((item: any, index: number) => (
                                    //         <li key={index} className={styles.employeeDetailsListItem}>
                                    //             <span className={styles.employeeDate}>
                                    //                 {dayjs(item.infringement_control_date).format('DD/MM/YYYY')}
                                    //             </span>
                                    //             <span className={styles.employeeName}>{item.error_name}</span>
                                    //         </li>
                                    //     ))}
                                    // </ul>
                                )}
                            </div>
                        )}
                    </div>
                </Panel>
            </div>
        </>
    )
}
